import React, { useEffect, useState } from 'react';
import { Building } from '../../../confi-state';
import { Segment } from '../models/segment';
import { ColumnService } from './column-service';
import { columnDefs, columnSubtype, subtypeEnum, variantEnum } from './models/column-definition';
import { columnType } from './models/column-type.enum';
import { headType } from './models/head-type.enum';
import { TowerColumn, PropsTower } from './models/props-config';
import { segmentType } from './models/segment-type';
import { partTypeEnum } from './models/part-type.enum';
import Refine from '../Refine/Refine';
import { RefineService } from '../Refine/refine.service';
import { ServiceManager } from '../../../services/service-manager';
import { buildingType } from '../models/building-type.enum';
import TowerPartSelector from './TowerPartSelector/TowerPartSelector';
import { SectionJSON } from '../../../models/section-json';
import TowerSegmentSelector from './TowerSegmentSelector/TowerSegmentSelector';

const buildingChange = (buildingProps: PropsTower, currentBuilding: Building, onChangeBuilding) => {
    onChangeBuilding(currentBuilding.projectId, currentBuilding.siteId, { ...currentBuilding, props: { ...buildingProps } }, true);
};

const TowerRefined = ({ currentBuilding, onChangeBuilding, sectionJson }: { currentBuilding: Building, onChangeBuilding: (projectId: number | string, siteId: number | string, building: Building, saveToDb: boolean) => void, sectionJson: SectionJSON }) => {

    const [buildingProps, setBuildingProps] = useState<PropsTower>(
        undefined
    );
    const [activePart, setActivePart] = useState<partTypeEnum>(
        partTypeEnum.bodyA
    );


    const getColumnTypes = (partType: partTypeEnum) => {
        let cols = [];
        if (partType === partTypeEnum.headLeft || partType === partTypeEnum.headRight) {
            cols = columnDefs.filter(s => s.columnSubtypes.includes(columnSubtype.head));
        } else if (partType === partTypeEnum.bodyA || partType === partTypeEnum.bodyB) {
            cols = columnDefs.filter(s => (s.columnSubtypes.includes(columnSubtype.body) || s.columnSubtypes.includes(columnSubtype.moosplaza)));
        } else if (partType === partTypeEnum.core) {
            cols = columnDefs.filter(s => (s.columnSubtypes.includes(columnSubtype.core)));
        }
        return cols.map(c => c.type);
    }

    const setUpDefaultColumnFromDefinition = (ct: headType | columnType) => {
        const coldef = columnDefs.find(c => c.type === ct)
        const columnService = new ColumnService();
        const segments = RefineService.getSegmentTypes(ct, columnDefs);
        if (segments && segments.length > 0) {
            let seg: Segment<segmentType, subtypeEnum, variantEnum>[] = []
            let amountOfUnits = [];
            segments.forEach(s => {
                const sg = RefineService.getSegmentDefinitionsForType(ct, s, columnDefs);
                const segDef = sg[0];
                let st = segDef.subtype;

                const floorDef = segDef.floorVariants[0];
                const floors = floorDef.floors;

                const variantDef = floorDef.variants[0];

                seg.push({
                    type: s as segmentType,
                    subtype: st,
                    sectionTypes: variantDef.sections,
                    floorsInSegment: floors
                })
            })
            const col: TowerColumn = {
                id: Math.random().toString(),
                type: ct,
                bays: coldef.bays,
                segments: seg,
                amountOfUnits
            }

            return col;
        }
    }

    const getDefaultColumnTypeForPart = (part) => {
        let type;
        if (part.type === partTypeEnum.bodyA || part.type === partTypeEnum.bodyB) {
            type = columnType.C_M;
        } else if (part.type === partTypeEnum.headLeft) {
            type = headType.H_2C_XL;
        } else if (part.type === partTypeEnum.headRight) {
            type = headType.H_2C_XL;
        }
        return type;
    }

    const updateBuildingColumn = (part, column) => {
        const bp = { ...buildingProps, partsRefined: [...buildingProps.partsRefined] };

        const partTypeToAdd = part.type;
        const partToAdd = bp.partsRefined[0].parts.find(p => p.type === partTypeToAdd);
        let columns = partToAdd.columns;

        columns = columns.map(col => col.id === column.id ? column : col);

        partToAdd.columns = [...columns.map(c => ({ ...c }))].sort((a, b) => a.order - b.order);

        let selectedHead = bp.partsRefined[0].parts.find(p => p.type === partTypeEnum.headLeft);
        let selectedHeadType;
        if (selectedHead && selectedHead.columns.length > 0) {
            const headCol = selectedHead.columns[0];
            selectedHeadType = headCol.type;
        }

        bp.partsRefined[0].parts = ServiceManager.babylonService.towerBabylonService.mapPartsTo3D(
            bp.partsRefined[0].parts,
            bp.moosPlazaAlignment,
            bp.moosPlazaPosition,
            true,
        );

        bp.info = ServiceManager.buildingHelperService.computePartInfo(bp.partsRefined[0].parts as any);

        const columnService = new ColumnService();
        columnService.computeAmountsForParts(bp.partsRefined[0].parts);
        bp.totalAmountOfAllUnits = columnService.getTotalAmountOfAllUnits(bp.partsRefined[0].parts);

        buildingChange(bp, currentBuilding, onChangeBuilding);
    }

    const updateBuildingColumns = (part, columns) => {
        const bp = { ...buildingProps, partsRefined: [...buildingProps.partsRefined] };

        const partToAdd = part.type;
        const partAdd = bp.partsRefined[0].parts.find(p => p.type === partToAdd);
        if (columns && columns.length > 0) {
            partAdd.columns = [...columns.map(c => ({ ...c }))].sort((a, b) => a.order - b.order);
        } else {
            partAdd.columns = [];
        }

        let selectedHead = bp.partsRefined[0].parts.find(p => p.type === partTypeEnum.headLeft);

        bp.partsRefined[0].parts = ServiceManager.babylonService.towerBabylonService.mapPartsTo3D(
            bp.partsRefined[0].parts,
            bp.moosPlazaAlignment,
            bp.moosPlazaPosition,
            true,
        );

        bp.info = ServiceManager.buildingHelperService.computePartInfo(bp.partsRefined[0].parts);
        const columnService = new ColumnService();
        columnService.computeAmountsForParts(bp.partsRefined[0].parts);
        bp.totalAmountOfAllUnits = columnService.getTotalAmountOfAllUnits(bp.partsRefined[0].parts);

        buildingChange(bp, currentBuilding, onChangeBuilding);
    }

    useEffect(() => {
        if (currentBuilding && currentBuilding.props) {
            const buildingProps = currentBuilding.props as PropsTower;
            if (buildingProps && (!buildingProps.partsRefined || buildingProps.partsRefined.length === 0)) {
                const bp: any = { ...buildingProps, parts: [...buildingProps.parts] }
                // currently this is a single entity, but it might be expanded in the future
                bp.partsRefined = [{
                    id: 'first', parts: ServiceManager.buildingHelperService.cloneParts(bp.parts)
                }];
                setBuildingProps({ ...bp });
                buildingChange({ ...bp }, currentBuilding, onChangeBuilding)
            } else {
                setBuildingProps(buildingProps);
            }
        }
        //eslint-disable-next-line
    }, [currentBuilding]);

    const getPartName = (p: partTypeEnum) => {
        let name = '';
        switch (p) {
            case partTypeEnum.headLeft:
                name = 'Kop 1'
                break;
            case partTypeEnum.bodyA:
                name = 'Body A'
                break;
            case partTypeEnum.bodyB:
                name = 'Body B'
                break;
            case partTypeEnum.headRight:
                name = 'Kop 2'
                break;
            case partTypeEnum.core:
                name = 'Core'
                break;
        }
        return name;
    }

    const getVariantName = (variant: variantEnum): string => {
        if (variant === variantEnum.left) {
            return 'Links';
        } else if (variant === variantEnum.right) {
            return 'Rechts';
        } else {
            return 'Midden';
        }
    }
    const getSegmentName = (p: segmentType) => {
        let name = '';
        switch (p) {
            case segmentType.plinth:
                name = 'Plint';
                break;
            case segmentType.middle:
                name = 'Midden';
                break;
            case segmentType.crown:
                name = 'Kroon'
                break;
        }
        return name;
    }

    const getSegmentSubtypeName = (p: subtypeEnum) => {
        let name = '';
        switch (p) {
            case subtypeEnum.C:
                name = 'Toren';
                break;
            case subtypeEnum.single:
                name = 'Geen'
                break;
        }
        return name;
    }

    return (
        <div className="group" data-testid="TowerRefined">
            {buildingProps && buildingProps.partsRefined &&
                <>
                    <div>
                        <h2 className="greyed-out-text">Gebouw verfijnen</h2>
                        <TowerPartSelector activePart={activePart} setActivePart={setActivePart} getPartName={getPartName} />
                        <Refine
                            part={buildingProps.partsRefined[0].parts.find(p => p.type === activePart)}
                            bt={currentBuilding.type}
                            buildingId={currentBuilding.id as string}
                            segmentTypesMultipleFloors={[segmentType.middle]}
                            sectionJson={sectionJson}
                            segmentTypesAllowCustom={[segmentType.plinth]}
                            columnDefs={columnDefs}
                            SegmentComponent={TowerSegmentSelector}
                            floorHeight={ServiceManager.buildingHelperService.floorHeightOfBuilding(buildingType.tower)}
                            getDefaultColumnTypeForPart={getDefaultColumnTypeForPart}
                            setUpDefaultColumnFromDefinition={setUpDefaultColumnFromDefinition}
                            updateBuildingColumn={updateBuildingColumn}
                            updateBuildingColumns={updateBuildingColumns}
                            getVariantName={getVariantName as any}
                            getSegmentName={getSegmentName as any}
                            getSegmentSubtypeName={getSegmentSubtypeName as any}
                        ></Refine>
                    </div>
                </>
            }
        </div>
    )
};

export default TowerRefined;
